import {
  APP_BASE_HREF,
  isPlatformBrowser,
  registerLocaleData,
} from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localePl from '@angular/common/locales/pl';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  Inject,
  Optional,
  PLATFORM_ID,
  provideZoneChangeDetection,
  REQUEST,
  TransferState,
} from '@angular/core';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { provideHotToastConfig } from '@ngneat/hot-toast';
import { provideQueryClientOptions } from '@ngneat/query';
import { authInterceptor } from 'src/app/_core/interceptors/auth.interceptor';
import { APP_ROUTES } from 'src/app/app.routes';
import { provideTranslations } from 'src/app/translate.provider';
import { HOT_TOAST_CONFIG } from './_core/utils/consts/hot-toast-config';
import { LanguageRepository } from './_core';
import {
  Language,
  setAppLangByUrl,
  LANGUAGE_KEY,
} from './_core/utils/helpers/set-app-lang-by-url';

registerLocaleData(localePl);
registerLocaleData(localeEn);

function getLanguage(
  transferState: TransferState,
  platformId: object,
  request?: Request
): string {
  let lang: Language | string;

  if (isPlatformBrowser(platformId)) {
    lang = setAppLangByUrl(window.location.pathname.split('/')[1]);
  } else {
    const url = request?.url || '';
    lang = setAppLangByUrl(url.split('/')[1]);
  }

  transferState.set(LANGUAGE_KEY, lang || 'pl');
  return `/${lang}`;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideClientHydration(withHttpTransferCacheOptions({})),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHotToastConfig(HOT_TOAST_CONFIG),
    provideRouter(
      APP_ROUTES,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({
        get scrollPositionRestoration() {
          if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window?.location?.search);
            if (params.get('search') || params.get('category')) {
              return 'disabled' as const;
            }
            return 'enabled' as const;
          } else {
            return 'enabled' as const;
          }
        },
        anchorScrolling: 'enabled',
      }),
      withComponentInputBinding()
    ),
    provideQueryClientOptions({
      defaultOptions: {
        queries: { retry: false, refetchOnWindowFocus: false },
        mutations: { retry: false },
      },
    }),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    ...provideTranslations,
    {
      provide: APP_BASE_HREF,
      useFactory: getLanguage,
      deps: [
        TransferState,
        [new Inject(PLATFORM_ID)],
        [new Optional(), new Inject(REQUEST)],
      ],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (langRepo: LanguageRepository) => () => {
        langRepo.initLangFromUrl();
        return Promise.resolve();
      },
      deps: [LanguageRepository],
      multi: true,
    },
  ],
};
